import { SMS_THREAD_PATIENT_PARAM } from "@/constants/paths";
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

interface UsePreselectedSmsPatientProps<T> {
  /** Conversations currently in the sidebar; `undefined` while they load. */
  patients?: T[];
  onSelect: (patient: T) => void;
}

/**
 * Opens the conversation named by the `patientId` deep-link once the sidebar has
 * loaded, then drops the param so a later manual pick is not overridden.
 */
export const usePreselectedSmsPatient = <T extends { uuid?: string }>({
  patients,
  onSelect,
}: UsePreselectedSmsPatientProps<T>): void => {
  const [searchParams, setSearchParams] = useSearchParams();
  const patientUuid = searchParams.get(SMS_THREAD_PATIENT_PARAM);
  const isConsumed = useRef(false);

  useEffect(() => {
    if (isConsumed.current || !patientUuid || !patients) return;

    isConsumed.current = true;
    const preselected = patients.find((it) => it?.uuid === patientUuid);
    if (preselected) onSelect(preselected);

    setSearchParams(
      (prev) => {
        prev.delete(SMS_THREAD_PATIENT_PARAM);
        return prev;
      },
      { replace: true }
    );
  }, [patientUuid, patients, onSelect, setSearchParams]);
};
